// import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
// import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import Reset from 'assets/images/Repertoire/Reset.svg';
import TempoIconLight from 'assets/images/Repertoire/TempoIconLight.svg';
import TempoIconLightAta from 'assets/images/Repertoire/TempoIconLightAta.svg';
import { Box, Slider, TextField, Typography } from '@mui/material';
import { RangeSlider } from './RangeSlider';
import { useDispatch, useSelector } from 'react-redux';
import { useHoldToRepeat } from 'Hooks/useHoldToRepeat';
import * as appActions from 'Actions/app';
import TempoPlus from 'assets/images/Repertoire/TempoPlus.png';
import TempoMinus from 'assets/images/Repertoire/TempoMinus.png';
import { useTempoContext } from 'Contexts/TempoContext';
import { Info, InfoOutline } from '@mui/icons-material';
import { MainAppReducer } from 'Types';
import { useState, useEffect, useCallback } from 'react';
import { CustomSwitch, CustomSwitchOutline } from './StyledComponents';
import { useFeatureFlag, FeatureFlag } from 'Utils/FeatureFlags';
import { sliderToTempo, sliderToTempoPercentage, tempoToSlider } from 'Utils/tempoSliderMapping';
import { LessonReducer } from 'Reducers/lessonReducer';

interface StyledSliderProps {
  handleSliderChange: (event: Event, value: number | number[]) => void;
  performanceTempo: number;
  sliderValue: number;
  setSliderValue: (value: number) => void;
  tempPctAdjustment: number;
  setTempPctAdjustment: (value: number) => void;
}

/** valueA = current tempo %, valueB = goal tempo % (identity-based; handles can cross) */
type StyledSliderRangeProps = {
  variant: 'range';
  /** Current tempo as percentage of performance (handle A) */
  sliderValueTempo: number;
  /** Goal tempo as percentage of performance (handle B) */
  sliderValueGoal: number;
  onRangeChange: (valueA: number, valueB: number) => void;
  min?: number;
  max?: number;
  /** Tooltip for the left handle (e.g. goal tempo) */
  tooltipLeft?: React.ReactNode;
  /** Tooltip for the right handle (e.g. current tempo) */
  tooltipRight?: React.ReactNode;
  /** Persistent label for the left handle (moves with handle) */
  labelLeft?: React.ReactNode;
  /** Persistent label for the right handle (moves with handle) */
  labelRight?: React.ReactNode;
  /** When true, left handle: label above and tooltip below (default: label below, tooltip on top). */
  flipLabelPositionLeft?: boolean;
  /** When true, right handle: label above and tooltip below (default: label below, tooltip on top). */
  flipLabelPositionRight?: boolean;
};

/** Single-handle mode: one value, normal slider behavior. */
type StyledSliderSingleProps = {
  variant: 'single';
  /** Current value (percentage or raw) */
  value: number;
  onChange: (value: number) => void;
  min?: number;
  max?: number;
  /** Optional label below the slider (defaults to valueLabelFormat(value)) */
  label?: React.ReactNode;
  /** Value label in thumb bubble and optional label below (e.g. "50%") */
  valueLabelFormat?: (value: number) => string;
};

export type StyledSliderVariantProps = StyledSliderRangeProps | StyledSliderSingleProps;

const sharedSliderStyles = {
  color: '#6F87F1',
  '& .MuiSlider-track': {
    color: '#2D3A67',
  },
  '& .MuiSlider-rail': {
    color: '#2D3A67',
  },
};

/**
 * Unified slider wrapper: use variant="single" for one handle (normal slider) or
 * variant="range" for two handles (current tempo / goal tempo). Shared styling for both.
 */
const StyledSliderVariant: React.FC<StyledSliderVariantProps> = (props) => {
  if (props.variant === 'single') {
    const {
      value,
      onChange,
      min = 0,
      max = 100,
      label,
      valueLabelFormat = (v) => `${Math.round(v)}%`,
    } = props;
    return (
      <Box sx={{ textAlign: 'center', margin: '20px 30px 10px 30px' }}>
        <Slider
          sx={sharedSliderStyles}
          value={value}
          onChange={(_e, val) => onChange(Array.isArray(val) ? val[0] : val)}
          min={min}
          max={max}
          valueLabelDisplay="auto"
          valueLabelFormat={valueLabelFormat}
          aria-labelledby="styled-slider-single"
        />
        {label != null ? (
          <Typography sx={{ mt: 0.5 }}>{label}</Typography>
        ) : (
          <Typography>{valueLabelFormat(value)}</Typography>
        )}
      </Box>
    );
  }

  const {
    sliderValueTempo,
    sliderValueGoal,
    onRangeChange,
    min = 0,
    max = 100,
    tooltipLeft,
    tooltipRight,
    labelLeft,
    labelRight,
    flipLabelPositionLeft,
    flipLabelPositionRight,
  } = props;

  return (
    <Box sx={{ textAlign: 'center', margin: '20px 30px 30px 10px' }}>
      <RangeSlider
        value={[sliderValueTempo, sliderValueGoal]}
        onChange={([left, right]) => onRangeChange(left, right)}
        min={min}
        max={max}
        ariaLabelLeft="Minimum tempo (percentage)"
        ariaLabelRight="Maximum tempo (percentage)"
        ariaLabelGroup="Tempo range"
        ariaValueTextLeft={(v) => `${Math.round(v)}%`}
        ariaValueTextRight={(v) => `${Math.round(v)}%`}
        thumbLeftClassName="tempo-range-thumb-left"
        thumbRightClassName="tempo-range-thumb-right"
        thumbLeftColor="#6F87F1"
        thumbRightColor="#469F6A"
        tooltipLeft={tooltipLeft}
        tooltipRight={tooltipRight}
        labelLeft={labelLeft}
        labelRight={labelRight}
        flipLabelPositionLeft={flipLabelPositionLeft}
        flipLabelPositionRight={flipLabelPositionRight}
        style={{
          track: { backgroundColor: '#2D3A67' },
          rail: { backgroundColor: '#2D3A67' },
        }}
      />
    </Box>
  );
};

const StyledSlider: React.FC<StyledSliderProps> = ({
  handleSliderChange,
  performanceTempo: _performanceTempo,
  sliderValue,
  setSliderValue: _setSliderValue,
  tempPctAdjustment,
  setTempPctAdjustment: _setTempPctAdjustment,
}) => {
  const sliderStyle = {
    color: '#6F87F1',
    '& .MuiSlider-track': {
      color: '#2D3A67',
    },
    '& .MuiSlider-rail': {
      color: '#2D3A67',
    },
  };
  return (
    <Box sx={{ textAlign: 'center', margin: '20px 30px 10px 30px' }}>
      <Slider
        sx={sliderStyle}
        value={sliderValue}
        onChange={handleSliderChange}
        min={0}
        max={100}
        aria-labelledby="continuous-slider"
        valueLabelFormat={(_x) => `${Math.round(sliderValue)}%`}
      />
      <Typography>{`${Math.round(tempPctAdjustment)}%`}</Typography>
    </Box>
  );
};

const controlsImagesStyles = {
  marginTop: '-1px',
  maxHeight: '54px',
};

type TempoControlsProps = {
  tempoToggleRef: React.RefObject<HTMLDivElement | null>;
  tempoContainerRef: React.RefObject<HTMLImageElement | null>;
  tempoInputRef: React.RefObject<HTMLInputElement | null>;
};

const TempoControls = ({
  tempoToggleRef,
  tempoContainerRef,
  tempoInputRef,
}: TempoControlsProps) => {
  const {
    //isExpanded,
    //setIsExpanded,
    tempo,
    performanceTempo,
    ataEnabled,
    max_tempo_value,
    min_tempo_value,
    mapTempoToSlider,
    changeTempoManually,
  } = useTempoContext();

  const [inputBoxText, setInputBoxText] = useState(tempo.toString());

  useEffect(() => {
    setInputBoxText(Math.round(tempo).toString());
  }, [tempo]);

  const [isExpanded, setIsExpanded] = useState(false);

  const commitTempoChange = useCallback(
    (text: string) => {
      changeTempoManually(Math.round(parseInt(text, 10)));
      /*console.debug('[change] commitTempoChange', text);
      const value = Math.max(Math.min(parseInt(text, 10), max_tempo_value), min_tempo_value);
      mapTempoToSlider(value, false);
      setInputBoxText(value.toString());*/
    },
    //[max_tempo_value, min_tempo_value, mapTempoToSlider],
    [changeTempoManually],
  );

  const handleClickOutside = useCallback(
    (e: MouseEvent) => {
      commitTempoChange(inputBoxText);
      const target = e.target as Node;
      const isInsideContainer = tempoContainerRef.current?.contains(target);
      const isInsideToggle = tempoToggleRef.current?.contains(target);
      if (!isInsideContainer && !isInsideToggle) {
        // Commit any pending manual tempo input before closing (input blur won't fire once we unmount)
        //setManualTempoChange();
        setIsExpanded(false);
      }
    },
    [
      tempoContainerRef,
      tempoToggleRef,
      setIsExpanded,
      mapTempoToSlider,
      inputBoxText,
      commitTempoChange,
    ],
  );

  useEffect(() => {
    if (isExpanded) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isExpanded, handleClickOutside]);

  /*if (useFeatureFlag(FeatureFlag.USE_ADAPTIVE_TEMPO)) {*/
  return (
    <Box id="tempo-controls-container" sx={{ position: 'relative' }}>
      {isExpanded && (
        <TempoContainer
          tempoContainerRef={tempoContainerRef}
          tempoInputRef={tempoInputRef}
          tempoToggleRef={tempoToggleRef}
          inputBoxText={inputBoxText}
          setInputBoxText={setInputBoxText}
          commitTempoChange={commitTempoChange}
        />
      )}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          width: '210px',
          backgroundColor: '#243056',
          borderRadius: '33px',
          padding: '3px',
          marginRight: '15px',
          '&:hover': {
            cursor: 'pointer',
            transition: '0.3s',
            borderRadius: '33px',
            boxShadow: '0px 0px 5px 5px rgb(222,144,118,.5)',
          },
        }}
        onClick={() => {
          setIsExpanded(!isExpanded);
        }}
        ref={tempoToggleRef}
      >
        <img
          style={{
            ...controlsImagesStyles,
            marginLeft: '1px',
            minHeight: '56px',
            marginTop: '1px',
          }}
          src={ataEnabled ? TempoIconLightAta : TempoIconLight}
        />
        <Box
          sx={{
            ...detailsTitleTextStyles,
            marginLeft: '10px',
            fontSize: '16px',
            display: 'flex',
            alignItems: 'center',
            color: '#ECECED',
            fontWeight: 700,
          }}
        >
          {Math.round(tempo)} BPM
          {tempo > performanceTempo ? (
            <Typography style={{ marginLeft: '8px', marginRight: '5px' }}> 🔥 </Typography>
          ) : (
            <Box
              style={{
                marginLeft: '8px',
                marginRight: '10px',
                width: '15px',
                height: '15px',
                borderRadius: '50%',
                backgroundColor: tempo >= (performanceTempo || 0) ? '#40E16D' : '#EB3459',
              }}
            />
          )}
          <KeyboardArrowDownIcon style={{ stroke: 'white', width: 28, strokeWidth: 0.8 }} />
        </Box>
      </Box>
    </Box>
  );
  /*} else {
    return <TempoContainerLarge isSmallScreen={false} freePlay={false} />;
  }*/
};

type TempoContainerProps = TempoControlsProps & {
  tempoContainerRef: React.RefObject<HTMLImageElement | null>;
  inputBoxText: string;
  setInputBoxText: (text: string) => void;
  commitTempoChange: (text: string) => void;
};

const TempoContainer = ({
  tempoContainerRef,
  tempoInputRef,
  inputBoxText,
  setInputBoxText,
  commitTempoChange,
}: TempoContainerProps) => {
  const {
    tempo,
    performanceTempo,
    sliderValueTempo,
    sliderValueGoal,
    setSliderValueTempo,
    setSliderValueGoal,
    //setSliderValue,
    //tempPctAdjustment,
    //setTempPctAdjustment,
    //manualTempoInput,
    //setManualTempoInput,
    //setManualTempoChange,
    max_tempo_value,
    min_tempo_value,
    goalTempo,
    setTempo,
    usingCustomGoalTempo,
    freePlayMode,
    resetTemp,
    changeTempoManually,
  } = useTempoContext();

  const handleInputBoxBlur = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      console.debug('[change] handleInputBoxBlur', e.target.value);
      commitTempoChange(e.target.value);
    },
    [commitTempoChange],
  );

  const topPos = 13; //getPosition().y
  const leftPos = 8; // getPosition().x

  const modalPosition = {
    left: `${leftPos - 32}px`, // Adjust as needed
    top: `${topPos + 80}px`, // Increased to match settings dropdown gap (tempo button is taller than settings icon)
  };

  // Press-and-hold behavior for tempo +/- buttons
  // Note: For RepertoirePlay, updates happen on each tick via mapTempoToSlider
  // (triggers tempo state chain → updateRepertoireDataLocal which is just local Redux state)
  // The main batching benefit is in Lesson.tsx which directly dispatches setTempo
  const { incrementHandlers, decrementHandlers } = useHoldToRepeat(
    tempo,
    //mapTempoToSlider, // Called on release (and during hold via onLocalChange)
    (finalTempo) => {
      changeTempoManually(finalTempo);
    },
    {
      min: min_tempo_value,
      max: max_tempo_value,
      onLocalChange: setTempo, // Update slider/percentage during hold
    },
  );

  /*const resetTemp = () => {
    dispatch(appActions.setTempo({ tempo: performanceTempo ?? 72, tier }));
    setTempo(performanceTempo ?? 72);
    setGoalTempo(performanceTempo ?? 72);
    setSliderValueTempo(50);
    setSliderValueGoal(50);
    //setTempPctAdjustment(100);
    //setSliderValue(50);
  };*/

  /*const handleManualTempoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setManualTempoInput(value);
  };*/

  /*useEffect(() => {
    setManualTempoInput(tempo.toString());
    if (repertoireProgressSelected) {
      dispatch(
        repertoireActions.updateRepertoireDataLocal({
          id: repertoireProgressSelected.id,
          last_tempo: tempo,
        }),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, tempo]);*/

  return (
    <Box
      id="rep-tempo-container"
      sx={{
        position: 'absolute',
        zIndex: 1500,
        ...modalPosition,
        minWidth: '350px',
        borderRadius: '8px',
        boxShadow:
          '0px 2px 4px -1px rgba(0,0,0,0.2), 0px 4px 5px 0px rgba(0,0,0,0.14), 0px 1px 10px 0px rgba(0,0,0,0.12)',
        padding: '16px',
        backgroundColor: '#050B13',
        animation: 'fadeInSlideDown 0.2s ease-out',
        '@keyframes fadeInSlideDown': {
          '0%': {
            opacity: 0,
            transform: 'translateY(-10px)',
          },
          '100%': {
            opacity: 1,
            transform: 'translateY(0)',
          },
        },
      }}
      ref={tempoContainerRef}
    >
      <Box
        sx={{
          position: 'absolute',
          top: '-10px', // Half of the carot size to move it above the modal
          left: '40px', // Align this with the hamburger icon
          width: '0',
          height: '0',
          borderLeft: '16px solid transparent',
          borderRight: '16px solid transparent',
          borderBottom: '16px solid #050B13', // Carot color should match modal background
        }}
      />

      <Box sx={detailsTitleTextStyles}>
        <Box
          sx={{
            textAlign: 'center',
            marginTop: '6px',
            marginBottom: '16px',
            fontSize: '20px',
            position: 'relative',
          }}
        >
          TEMPO
        </Box>

        <Box
          sx={{
            textAlign: 'center',
            marginTop: '30px',
            marginBottom: '16px',
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '10px',
          }}
        >
          <Box
            sx={{
              display: 'inline-block',
              '&:hover': { cursor: 'pointer' },
              userSelect: 'none',
              WebkitTouchCallout: 'none',
              touchAction: 'manipulation',
            }}
            {...decrementHandlers}
          >
            <img
              style={{ maxHeight: '28px', verticalAlign: 'top', pointerEvents: 'none' }}
              src={TempoMinus}
              draggable={false}
            />
          </Box>
          <Box sx={{ margin: '0px 20px', display: 'flex', fontSize: '24px', alignItems: 'start' }}>
            <Box sx={{ marginTop: '-3px', marginRight: '1px' }}>
              {/* {tempo} */}
              <TextField
                inputRef={tempoInputRef}
                type="number"
                // value={tempo}
                value={inputBoxText}
                onChange={(e) => setInputBoxText(e.target.value)}
                onBlur={handleInputBoxBlur}
                inputProps={{
                  // min: 0,//10,
                  // max: 9999999,//300,
                  style: {
                    textAlign: 'left',
                    color: '#FFFFFF',
                    fontFamily: 'Lato',
                    fontWeight: 400,
                    fontSize: '24px',
                    lineHeight: '24px',
                    letterSpacing: '1px',
                    height: '0px',
                    margin: 0,
                    paddingLeft: '3px',
                    paddingRight: '3px',
                  },
                }}
                sx={{
                  width: '80px',
                  textAlign: 'center',
                  '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button':
                    {
                      '-webkit-appearance': 'none',
                      margin: 0,
                    },
                  '& input[type=number]': {
                    '-moz-appearance': 'textfield',
                  },
                  maxWidth: '54px',
                  marginLeft: 0,
                  padding: 0,
                }}
              />
            </Box>{' '}
            <Box sx={{ fontSize: '16px' }}>BPM</Box>
          </Box>
          <Box
            sx={{
              display: 'inline-block',
              '&:hover': { cursor: 'pointer' },
              userSelect: 'none',
              WebkitTouchCallout: 'none',
              touchAction: 'manipulation',
            }}
            {...incrementHandlers}
          >
            <img
              style={{ maxHeight: '28px', verticalAlign: 'top', pointerEvents: 'none' }}
              src={TempoPlus}
              draggable={false}
            />
          </Box>
        </Box>

        <Box
          sx={{
            textAlign: 'center',
            marginTop: '32px',
            marginBottom: '8px',
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          {tempo == (performanceTempo ?? 72) ? (
            <Box sx={{ textAlign: 'center', display: 'flex', alignItems: 'center', gap: '7px' }}>
              <Box
                style={{
                  width: '15px',
                  height: '15px',
                  borderRadius: '50%',
                  backgroundColor: '#40E16D',
                }}
              ></Box>{' '}
              At Tempo
            </Box>
          ) : tempo < (performanceTempo ?? 72) ? (
            <Box sx={{ textAlign: 'center', display: 'flex', alignItems: 'center', gap: '7px' }}>
              <Box
                style={{
                  width: '15px',
                  height: '15px',
                  borderRadius: '50%',
                  backgroundColor: '#EB3459',
                }}
              ></Box>{' '}
              Below Tempo
            </Box>
          ) : (
            <Box sx={{ textAlign: 'center', display: 'flex', alignItems: 'center', gap: '7px' }}>
              <Typography style={{ marginLeft: '8px', marginRight: '5px' }}> 🔥 </Typography>
              Above Tempo
            </Box>
          )}
        </Box>

        <Box
          sx={{
            textAlign: 'center',
            marginTop: '6px',
            marginBottom: '16px',
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/*<StyledSlider
            handleSliderChange={handleSliderChange}
            performanceTempo={performanceTempo ?? 0}
            sliderValue={sliderValue}
            setSliderValue={setSliderValue}
            tempPctAdjustment={tempPctAdjustment}
            setTempPctAdjustment={setTempPctAdjustment}
          />*/}
          {useFeatureFlag(FeatureFlag.USE_ADAPTIVE_TEMPO) &&
          freePlayMode &&
          usingCustomGoalTempo ? (
            <StyledSliderVariant
              variant="range"
              sliderValueTempo={sliderValueTempo}
              sliderValueGoal={sliderValueGoal}
              onRangeChange={(left, right) => {
                const tempoValue = Math.round(
                  sliderToTempo(left / 100, min_tempo_value, performanceTempo, max_tempo_value),
                );
                changeTempoManually(tempoValue);
                setSliderValueGoal(right);
                setSliderValueTempo(left);
              }}
              min={0}
              max={100}
              tooltipLeft="Current tempo"
              tooltipRight="Goal tempo"
              flipLabelPositionLeft={true}
              labelLeft={tempo}
              labelRight={goalTempo}
            />
          ) : (
            <StyledSliderVariant
              variant="single"
              value={sliderValueTempo}
              onChange={(value) => {
                const tempoValue = sliderToTempo(
                  value / 100,
                  min_tempo_value,
                  performanceTempo,
                  max_tempo_value,
                );
                changeTempoManually(Math.round(tempoValue));
                setSliderValueTempo(value);
              }}
              min={0}
              max={100}
              label="Tempo"
              valueLabelFormat={(value) =>
                `${Math.round(sliderToTempoPercentage(value / 100, min_tempo_value, performanceTempo, max_tempo_value))}%`
              }
            />
          )}
        </Box>

        <Box
          sx={{
            textAlign: 'center',
            marginTop: '30px',
            marginBottom: '16px',
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0px',
          }}
        >
          <Box
            sx={{ magin: 0, padding: 0, '&:hover': { cursor: 'pointer' } }}
            onClick={resetTemp}
            id="rep-reset-button"
          >
            <img style={{ width: '32px' }} src={Reset} />
          </Box>
          Reset
        </Box>
        <AutoTempoAdjustmentSettings />
      </Box>
    </Box>
  );
};

const AutoTempoAdjustmentSettings = () => {
  const { ataEnabled, setAtaEnabled, usingCustomGoalTempo, setUsingCustomGoalTempo, freePlayMode } =
    useTempoContext();
  const [isAtaInfoShown, setIsInfoShown] = useState(false);
  const [isCustomGoalInfoShown, setIsCustomGoalInfoShown] = useState(false);

  const handleAutoTempoAdjustmentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setAtaEnabled(e.target.checked);
  };

  const handleUsingCustomGoalTempoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setUsingCustomGoalTempo(e.target.checked);
  };

  const handleCustomGoalInfoButtonClick = () => {
    setIsCustomGoalInfoShown(!isCustomGoalInfoShown);
  };

  const handleAtaInfoButtonClick = () => {
    setIsInfoShown(!isAtaInfoShown);
  };

  if (!useFeatureFlag(FeatureFlag.USE_ADAPTIVE_TEMPO)) {
    return null;
  }
  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          marginLeft: '15px',
          marginRight: '15px',
          padding: '5px',
          borderTop: '1px solid #333333',
        }}
      >
        <CustomSwitch checked={ataEnabled} onChange={handleAutoTempoAdjustmentChange} />
        <Typography sx={{ flex: 1 }}>Auto Tempo Adjustment</Typography>
        <InfoOutline
          onClick={handleAtaInfoButtonClick}
          sx={{ color: isAtaInfoShown ? '#6F87F1' : 'white' }}
        />
      </Box>
      {isAtaInfoShown && (
        <Box
          sx={{
            backgroundColor: 'rgba(50, 53, 62, 1)',
            padding: '15px',
            borderRadius: '8px',
          }}
        >
          <Typography>
            Auto Tempo Adjustment automatically slows down and accelerates your tempo based on your
            performance.
          </Typography>
        </Box>
      )}
      {ataEnabled && freePlayMode && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            marginLeft: '15px',
            marginRight: '15px',
            padding: '5px',
          }}
        >
          <CustomSwitchOutline
            checked={usingCustomGoalTempo}
            onChange={handleUsingCustomGoalTempoChange}
          />
          <Typography sx={{ flex: 1 }}>Customise Goal Tempo</Typography>
          <InfoOutline
            onClick={handleCustomGoalInfoButtonClick}
            sx={{ color: isCustomGoalInfoShown ? '#6F87F1' : 'white' }}
          />
        </Box>
      )}
      {isCustomGoalInfoShown && (
        <Box
          sx={{
            backgroundColor: 'rgba(50, 53, 62, 1)',
            padding: '15px',
            borderRadius: '8px',
          }}
        >
          <Typography>In Free Play, you can set whatever goal tempo you’d like.</Typography>
        </Box>
      )}
    </Box>
  );
};

const detailsTitleTextStyles = {
  color: '#FFFFFF',
  fontFamily: 'Lato',
  fontSize: '15px',
  fontHeight: '700',
  lineHeight: '24px',
  letterSpacing: '1px',
  textAlign: 'left',
};

export default TempoControls;
